import { useMemo } from 'react';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

import {
	isNudgeByAction,
	type NudgeEntry,
} from '@confluence/page-rovo-config-common/entry-points/nudgeTypes.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';

/**
 * Returns the list of nudge actions to register for the current edit page.
 * Only applicable to live docs (controlled by enableLiveDocNudges).
 * Returns [] if live doc nudges are not enabled.
 * The returned array is ready to pass directly to setNudges.
 */
export function useEditPageNudgeConfig(
	enableLiveDocNudges: boolean,
	eligibleForChangeboarding: boolean,
	isFirstVisit: boolean,
	isCurrentUserContributor: boolean,
	isEligibilityLoading: boolean,
): ConversationAction[] {
	const viewPageRovoActions = useViewPageRovoActions();

	const actionsById = useMemo(
		() =>
			new Map<string, ConversationAction>(
				viewPageRovoActions.map(
					(registeredAction: { actionId: string; action: ConversationAction }) => [
						registeredAction.actionId,
						registeredAction.action,
					],
				),
			),
		[viewPageRovoActions],
	);

	return useMemo(() => {
		if (!enableLiveDocNudges) return [];

		const isAudioBriefingEligible =
			!isEligibilityLoading &&
			!eligibleForChangeboarding &&
			isFirstVisit &&
			!isCurrentUserContributor;

		const nudgeEntries: NudgeEntry[] = [
			{ actionId: 'audio-briefing', enabled: isAudioBriefingEligible },
		];

		return nudgeEntries.reduce<ConversationAction[]>((acc, nudgeEntry) => {
			if (!nudgeEntry.enabled) return acc;
			const action = isNudgeByAction(nudgeEntry)
				? nudgeEntry.action
				: actionsById.get(nudgeEntry.actionId);
			if (action) acc.push(action);
			return acc;
		}, []);
	}, [
		enableLiveDocNudges,
		isEligibilityLoading,
		eligibleForChangeboarding,
		isFirstVisit,
		isCurrentUserContributor,
		actionsById,
	]);
}
